import type { CrudAction as CrudActionName, FieldAction } from '#layers/access/types/permissions/crud';
import type { MonospacePolicyEntitlementResource, PolicyDocument, RulesPolicyDocument } from '#layers/access/types/permissions/policy';
import type { EntitlementKeyParts } from '#layers/access/utils/entitlement-codec';
import type { GlobalCollectionId, GlobalFieldId, GlobalSingleRelationFieldId, ResolvedMonospaceSingleRelationField } from '#layers/data-model/public';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { createError } from '#app/composables/error';
import { useNuxtApp } from '#app/nuxt';
import { useStore } from '#imports';
import { FIELD_WILDCARD } from '#layers/access/constants/permissions';
import { CrudAction } from '#layers/access/constants/permissions/crud';
import { useAuth } from '#layers/access/stores/auth';
import { fieldActions } from '#layers/access/types/permissions/crud';
import { buildGrantIndex, allowedResourceIds as grantedResourceIds, isGranted } from '#layers/access/utils/entitlement-codec';
import { isAdminPolicyDocument } from '#layers/access/utils/policies';
import { createRequestTracker } from '#layers/base/public';
import { getRelationConstraint, getRelationConstraintFields } from '#layers/data-model/public';
import { useWorkspaceSchemaStore, useWorkspaceStore } from '#layers/workspace/public';

const FIELD_WILDCARD_SYMBOL = Symbol('wildcard');

function findActionByName(
	resource: MonospacePolicyEntitlementResource,
	actionName: string,
): MonospacePolicyEntitlementResource['actions'][number] | undefined {
	// Guard against a resource that slipped past validation without an `actions` array:
	// a missing/non-array `actions` must fail closed (return undefined), never throw on the
	// authorization hot path.
	return (resource.actions ?? []).find((action) => action.name === actionName);
}

/** Whether the resource declares the named action. */
function descriptorHasAction(resource: MonospacePolicyEntitlementResource, actionName: string): boolean {
	return findActionByName(resource, actionName) !== undefined;
}

/** Whether the named action is scoped to an identifier (per-record rather than blanket). */
function isScoped(resource: MonospacePolicyEntitlementResource, actionName: string): boolean {
	return findActionByName(resource, actionName)?.hasIdentifier ?? false;
}

type FieldPermissionValue = Set<GlobalFieldId> | typeof FIELD_WILDCARD_SYMBOL;

// Merged item permissions: CollectionId → Set of allowed actions
type ItemPermissionMap = Map<GlobalCollectionId, Set<CrudActionName>>;

// Merged field permissions: CollectionId → Action → (Set<FieldId> | FIELD_WILDCARD_SYMBOL)
type FieldPermissionMap = Map<GlobalCollectionId, Map<FieldAction, FieldPermissionValue>>;

export const usePermissionsStore = defineStore('permissions', () => {
	const store = useStore();
	const { currentUser } = useAuth();
	const { onBeforeWorkspaceChanged, onWorkspaceChanged, currentWorkspaceSlug } = useWorkspaceStore();

	const isLoading = ref(false);
	const isInitialized = ref(false);
	const initializationRequests = createRequestTracker();

	// The descriptor is required to gate covered collections. Until it has SUCCESSFULLY loaded for
	// the current workspace, entitlement checks fail closed for non-admins (a failed/never-loaded
	// descriptor is not the same as "nothing is gated"). Set true only after a clean initialize().
	const descriptorLoaded = ref(false);

	// Workspace-wide entitlement descriptor (which collections/actions are gated). `authorize`
	// denies non-admins while it is still pending or has not loaded, so the store both drives its
	// load and re-exposes its loading/error state. The store is a per-Nuxt-context singleton,
	// so the query lives directly on it and is reset by the workspace-change hooks below.
	// Gated on an active workspace: this workspace-scoped endpoint has no meaningful URL until a
	// workspace is hydrated, so firing before then (e.g. creating the first workspace on a fresh
	// instance) requests `/entitlements` with no workspace prefix and 404s. initialize() refreshes
	// it once `onWorkspaceChanged` fires with the slug set.
	const {
		data: descriptorResources,
		loading: descriptorPending,
		error: descriptorError,
		refresh: refreshDescriptor,
	} = store.WorkspaceEntitlementResource.query((q) => q.many(currentWorkspaceSlug.value ? {} : { enabled: false }));

	/** The descriptor entry gating the given collection, if any. */
	function resourceForCollection(collectionId: GlobalCollectionId): MonospacePolicyEntitlementResource | undefined {
		return descriptorResources.value.find((resource) => resource.collectionId === collectionId);
	}

	// Drop the prior workspace's grants before the swap so no reactive surface can flash them while
	// the new workspace initializes; initialize() then republishes once the new fetch resolves.
	onBeforeWorkspaceChanged(() => {
		clear();
	});

	onWorkspaceChanged(async () => {
		// Unloading (navigating away from every workspace) fires this with no active workspace. The
		// workspace-scoped queries are disabled without a slug, so initialize() would refetch nothing
		// yet still declare the store loaded against empty grants. Reset instead.
		if (!currentWorkspaceSlug.value) {
			clear();
			return;
		}

		await initialize();
	});

	// Query user roles with nested policies
	const { data: userRoles, error: rolesError, refresh } = store.MonospaceUserRole.query(({ many }) =>
		many(!currentUser.value || !currentWorkspaceSlug.value
			? { enabled: false }
			: {
					filter: { userId: currentUser.value.id },
					include: {
						role: {
							ancestors: {
								ancestor: {
									policies: { policy: true },
								},
							},
							policies: { policy: true },
						},
					},
				}),
	);

	// Extract policies from role hierarchy (both direct and ancestor policies)
	const policies = computed<PolicyDocument[]>(() => {
		const result: PolicyDocument[] = [];

		for (const userRole of userRoles.value) {
			const role = userRole.role;
			if (!role) continue;

			// Direct role policies
			if (role.policies) {
				for (const { policy } of role.policies) {
					if (policy?.content) {
						result.push(policy.content as PolicyDocument);
					}
				}
			}

			// Ancestor policies
			if (role.ancestors) {
				for (const { ancestor } of role.ancestors) {
					if (!ancestor?.policies) continue;

					for (const { policy } of ancestor.policies) {
						if (policy?.content) {
							result.push(policy.content as PolicyDocument);
						}
					}
				}
			}
		}

		return result;
	});

	const isAdmin = computed(() => policies.value.some((policy) => isAdminPolicyDocument(policy)));

	// Rules policies drive every non-admin gate (item/field rules and entitlements); admin policies
	// carry no rules and are short-circuited by `isAdmin`, so filter them out once here.
	const rulesPolicies = computed(() =>
		policies.value.filter((policy): policy is RulesPolicyDocument => !isAdminPolicyDocument(policy)),
	);

	// Pre-computed merged item permissions
	const mergedItemPermissions = computed<ItemPermissionMap>(() => {
		const result = new Map<GlobalCollectionId, Set<CrudActionName>>();

		for (const policy of rulesPolicies.value) {
			if (!policy.itemRules) continue;

			for (const [collectionId, rules] of Object.entries(policy.itemRules)) {
				const existing = result.get(collectionId as GlobalCollectionId) ?? new Set();

				if (rules.create !== undefined) existing.add(CrudAction.Create);
				if (rules.read !== undefined) existing.add(CrudAction.Read);
				if (rules.update !== undefined) existing.add(CrudAction.Update);
				if (rules.delete !== undefined) existing.add(CrudAction.Delete);

				result.set(collectionId as GlobalCollectionId, existing);
			}
		}

		return result;
	});

	// Pre-computed merged field permissions (handles wildcard merging)
	const mergedFieldPermissions = computed<FieldPermissionMap>(() => {
		const result: FieldPermissionMap = new Map();

		for (const policy of rulesPolicies.value) {
			if (!policy.fieldRules) continue;

			for (const [collectionId, rules] of Object.entries(policy.fieldRules)) {
				const colId = collectionId as GlobalCollectionId;
				let actionMap = result.get(colId);

				if (!actionMap) {
					actionMap = new Map();
					result.set(colId, actionMap);
				}

				for (const action of fieldActions) {
					const fieldRules = rules[action];
					if (!fieldRules) continue;

					const existing = actionMap.get(action);

					// Wildcard always wins (most permissive)
					if (fieldRules === FIELD_WILDCARD || existing === FIELD_WILDCARD_SYMBOL) {
						actionMap.set(action, FIELD_WILDCARD_SYMBOL);
						continue;
					}

					// Merge field IDs (OR logic)
					const fieldIds = existing ?? new Set<GlobalFieldId>();

					for (const fieldId of Object.keys(fieldRules)) {
						fieldIds.add(fieldId as GlobalFieldId);
					}

					actionMap.set(action, fieldIds);
				}
			}
		}

		return result;
	});

	const grantIndex = computed(() => {
		const grants: string[] = [];

		for (const policy of rulesPolicies.value) {
			if (policy.entitlements) grants.push(...policy.entitlements);
		}

		return buildGrantIndex(grants);
	});

	function hasEntitlement(request: EntitlementKeyParts): boolean {
		if (isAdmin.value) return true;
		// Fail closed until the descriptor has loaded: an empty/never-fetched grant set is
		// indistinguishable from "no grants", so deny rather than leak the prior workspace's state.
		if (!descriptorLoaded.value) return false;
		return isGranted(grantIndex.value, request);
	}

	/** The ids the user may act on for a scoped entitlement (e.g. role:assign), or '*' for all. */
	function allowedResourceIds(resource: string, action: string): string[] | '*' {
		if (isAdmin.value) return '*';
		if (!descriptorLoaded.value) return [];
		return grantedResourceIds(grantIndex.value, resource, action);
	}

	function canActionCollection(collectionId: GlobalCollectionId, action: CrudActionName): boolean {
		if (isAdmin.value) return true;
		return mergedItemPermissions.value.get(collectionId)?.has(action) ?? false;
	}

	function canCreate(collectionId: GlobalCollectionId): boolean {
		return canActionCollection(collectionId, CrudAction.Create);
	}

	function canRead(collectionId: GlobalCollectionId): boolean {
		return canActionCollection(collectionId, CrudAction.Read);
	}

	function canUpdate(collectionId: GlobalCollectionId): boolean {
		return canActionCollection(collectionId, CrudAction.Update);
	}

	function canDelete(collectionId: GlobalCollectionId): boolean {
		return canActionCollection(collectionId, CrudAction.Delete);
	}

	function hasAnyAccess(collectionId: GlobalCollectionId): boolean {
		if (isAdmin.value) return true;
		const perms = mergedItemPermissions.value.get(collectionId);
		return perms ? perms.size > 0 : false;
	}

	function canActionField(collectionId: GlobalCollectionId, field: GlobalFieldId | { id: GlobalFieldId }, action: FieldAction): boolean {
		if (isAdmin.value) return true;
		const fieldId = typeof field === 'string' ? field : field.id;
		const allowed = mergedFieldPermissions.value.get(collectionId)?.get(action);
		if (!allowed) return false;
		return allowed === FIELD_WILDCARD_SYMBOL || allowed.has(fieldId);
	}

	function canReadField(collectionId: GlobalCollectionId, field: GlobalFieldId | { id: GlobalFieldId }): boolean {
		if (isAdmin.value) return true;
		const fieldId = typeof field === 'string' ? field : field.id;

		const { findSingleRelationFieldById } = useWorkspaceSchemaStore();
		const relationField = findSingleRelationFieldById(fieldId as GlobalSingleRelationFieldId);

		if (relationField) {
			return canReadRelationField(relationField);
		}

		return canActionField(collectionId, fieldId, CrudAction.Read);
	}

	function canReadRelationField(field: ResolvedMonospaceSingleRelationField): boolean {
		const { thisSideConstrained, constraint } = getRelationConstraint(field);
		if (!constraint) return false;

		const { constrainedFields } = getRelationConstraintFields(constraint);
		if (constrainedFields.length === 0) return false;

		if (thisSideConstrained) {
			return constrainedFields.every((constrainedField) => canActionField(field.collectionId, constrainedField.id, CrudAction.Read));
		}

		return canRead(field.oppositeCollectionId)
			&& constrainedFields.every((constrainedField) => canActionField(field.oppositeCollectionId, constrainedField.id, CrudAction.Read));
	}

	function canCreateField(collectionId: GlobalCollectionId, field: GlobalFieldId | { id: GlobalFieldId }): boolean {
		return canActionField(collectionId, field, CrudAction.Create);
	}

	function canUpdateField(collectionId: GlobalCollectionId, field: GlobalFieldId | { id: GlobalFieldId }): boolean {
		return canActionField(collectionId, field, CrudAction.Update);
	}

	function canCreateFields(collectionId: GlobalCollectionId, fieldIds: GlobalFieldId[]): boolean {
		return fieldIds.every((fieldId) => canCreateField(collectionId, fieldId));
	}

	function canUpdateFields(collectionId: GlobalCollectionId, fieldIds: GlobalFieldId[]): boolean {
		return fieldIds.every((fieldId) => canUpdateField(collectionId, fieldId));
	}

	async function initialize() {
		isLoading.value = true;
		// Treat a workspace switch as not-yet-loaded: drop the prior workspace's grants before
		// refetching so consumers (and the entitlement-gate middleware) deny until the new
		// workspace's roles AND descriptor have loaded.
		isInitialized.value = false;
		descriptorLoaded.value = false;

		const request = initializationRequests.start();

		try {
			await Promise.all([refresh(), refreshDescriptor()]);

			// rstore's refresh() resolves even on HTTP/network error (it catches internally and
			// sets an `error` ref). A resolved-but-errored fetch must fail closed with a visible
			// signal, not silently leave an empty descriptor that degrades covered collections to
			// the item-rule path. Surface it as a full-page error via the workspace-switch chain.
			if (descriptorError.value || rolesError.value) {
				throw createError({
					statusCode: 500,
					statusMessage: useNuxtApp().$i18n.t('permissions.load_failed'),
				});
			}

			if (!request.isCurrent()) return;

			descriptorLoaded.value = true;
			isInitialized.value = true;
		}
		finally {
			if (request.isCurrent()) isLoading.value = false;
		}
	}

	function clear() {
		initializationRequests.invalidate();
		isInitialized.value = false;
		descriptorLoaded.value = false;
		// clear() can interrupt an in-flight initialize() that never resolves;
		// without this the store stays stuck in the loading state.
		isLoading.value = false;
	}

	return {
		isLoading,
		isInitialized,
		descriptorPending,
		descriptorLoaded,
		resources: descriptorResources,
		resourceForCollection,
		descriptorHasAction,
		isScoped,
		isAdmin,
		initialize,
		clear,
		hasAnyAccess,
		hasEntitlement,
		allowedResourceIds,
		canActionCollection,
		canCreate,
		canRead,
		canUpdate,
		canDelete,
		canReadField,
		canCreateField,
		canUpdateField,
		canCreateFields,
		canUpdateFields,
	};
});

export type PermissionsStore = ReturnType<typeof usePermissionsStore>;
